/**
 * AisDataReceiver.js
 * 处理AIS数据接收和解析的专用模块
 */
import Vue from 'vue';
import appConfig from '@/config/app-config';
import signalRManager from './signalr-manager'; // 改为导入signalRManager
import { cleanupExpiredShips } from './mapLayerRenderer'; // 导入船舶出清函数

// 事件总线，用于发布船舶数据更新事件
const eventBus = new Vue();

/**
 * 清洗 AIS 船名：AIS 报文用 @ 填充固定长度字段，需去除 @ 填充符、
 * 控制字符，并压缩连续空格。保留中文等可打印字符。
 * @param {string} name 原始船名
 * @returns {string|null} 清洗后的船名，清洗后为空时返回 null
 */
export function sanitizeVesselName(name) {
  if (!name) return null;
  const cleaned = String(name)
    .replace(/@/g, '')
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned || null;
}

class AisDataReceiver {
  constructor() {
    this._ships = [];
    this._isConnected = false;
    this._isSubscribed = false;
    this._signalR = null;
    this._options = appConfig.signalR.options;
    this._serverUrl = appConfig.signalR.serverUrl;
    this._topic = appConfig.ais.topic;
    this._messageType = appConfig.ais.messageType;
    
    // 添加数据缓冲和节流控制
    this._bufferEnabled = true;  // 默认启用缓冲
    this._updateInterval = 1000; // 默认1秒更新一次
    this._bufferTimer = null;    // 缓冲定时器
    this._pendingUpdates = [];   // 待处理的更新
    this._mapReady = false;      // 地图就绪状态
    this._lastEmitTime = 0;      // 上次发送事件的时间
    this._throttleInterval = 300; // 默认节流间隔300ms
    
    // 连接和订阅重试
    this._connectingPromise = null;
    this._subscribingPromise = null;
    this._connectRetryCount = 0;
    this._subscribeRetryCount = 0;
    this._maxConnectRetries = 5;
    this._maxSubscribeRetries = 3;
    this._connectRetryInterval = 2000; // 2秒
    this._subscribeRetryInterval = 1500; // 1.5秒
    
    // 船舶出清节流控制
    this._lastCleanupTime = 0; // 上次清理时间
    this._cleanupThrottleInterval = 10000; // 10秒清理一次
  }

  /**
   * 初始化AIS数据接收器
   * @param {Object} vueInstance - Vue实例
   * @param {Object} options - 配置选项
   */
  init(vueInstance, options = {}) {
    // 合并选项
    this._options = {
      ...this._options,
      ...options
    };
    
    // 使用signalRManager而非直接创建SignalRClient实例
    this._signalR = signalRManager;
    
    // 确保signalRManager已初始化
    if (!this._signalR._isInitialized) {
      this._signalR.init(this._options);
    }
    
    console.log('AisDataReceiver已初始化，服务器地址:', this._serverUrl);
  }

  /**
   * 连接到服务器并订阅AIS数据
   * @returns {Promise<void>}
   */
  async connect() {
    if (!this._signalR) {
      throw new Error('请先调用init方法初始化AisDataReceiver');
    }

    // 注册AIS消息处理器 - 使用signalRManager的onMessage方法
    console.log('AisDataReceiver注册AIS消息处理器:', this._messageType);
    this._signalR.onMessage(this._messageType, this._handleShipMessage.bind(this));
    
    // 注册连接状态变更处理 - 使用signalRManager的onConnectionStatusChanged方法
    this._signalR.onConnectionStatusChanged(this._handleConnectionStatusChanged.bind(this));
    
    // 如果已有连接中的Promise，则返回该Promise
    if (this._connectingPromise) {
      console.log('AIS连接已在进行中，等待连接完成...');
      return this._connectingPromise;
    }
    
    // 创建连接Promise
    this._connectingPromise = (async () => {
      try {
        console.log(`AisDataReceiver正在连接到服务器: ${this._serverUrl}`);
        
        // 不再使用延迟，直接连接
        // 确保连接 - 使用signalRManager的connect方法而非ensureConnection
        if (!this._signalR.isConnected) {
          await this._signalR.connect(this._serverUrl, {
            clientType: 'AIS_RECEIVER',
            clientId: 'ais_' + Date.now()
          });
          console.log('AIS数据接收器连接成功');
        } else {
          console.log('SignalR已连接，复用现有连接');
        }
        
        // 连接成功后，尝试订阅
        await this._subscribeWithRetry();
        
        this._isConnected = true;
        console.log('AisDataReceiver已连接并订阅船舶数据主题');
        
        // 重置重试计数器
        this._connectRetryCount = 0;
        this._subscribeRetryCount = 0;
        
        return true;
      } catch (error) {
        console.error('AisDataReceiver连接或订阅失败:', error);
        
        // 检查连接重试次数
        if (this._connectRetryCount < this._maxConnectRetries) {
          this._connectRetryCount++;
          console.log(`AIS连接失败，${this._connectRetryInterval/1000}秒后进行第${this._connectRetryCount}次重试...`);
          
          // 延迟后重试
          await new Promise(resolve => setTimeout(resolve, this._connectRetryInterval));
          this._connectingPromise = null; // 清除当前Promise
          return this.connect(); // 递归调用自身进行重试
        }
        
        throw error;
      } finally {
        this._connectingPromise = null; // 清除Promise引用
      }
    })();
    
    return this._connectingPromise;
  }

  /**
   * 带重试的订阅方法
   * @private
   */
  async _subscribeWithRetry() {
    // 如果已有订阅中的Promise，则返回该Promise
    if (this._subscribingPromise) {
      return this._subscribingPromise;
    }
    
    this._subscribingPromise = (async () => {
      try {
        // 检查连接状态
        if (!this._signalR.isConnected) {
          throw new Error('无法订阅：SignalR未连接');
        }
        
        console.log(`尝试订阅AIS数据主题: ${this._topic}`);
        await this._signalR.subscribe(this._topic);
        this._isSubscribed = true;
        console.log('AIS数据订阅成功');
        
        // 重置重试计数器
        this._subscribeRetryCount = 0;
        
        return true;
      } catch (error) {
        console.error('AIS数据订阅失败:', error);
        
        // 检查订阅重试次数
        if (this._subscribeRetryCount < this._maxSubscribeRetries) {
          this._subscribeRetryCount++;
          console.log(`订阅失败，${this._subscribeRetryInterval/1000}秒后进行第${this._subscribeRetryCount}次重试...`);
          
          // 延迟后重试
          await new Promise(resolve => setTimeout(resolve, this._subscribeRetryInterval));
          this._subscribingPromise = null; // 清除当前Promise
          return this._subscribeWithRetry(); // 递归调用自身进行重试
        }
        
        throw error;
      } finally {
        this._subscribingPromise = null; // 清除Promise引用
      }
    })();
    
    return this._subscribingPromise;
  }

  /**
   * 处理连接状态变化
   * @param {boolean} isConnected - 是否已连接
   * @param {string} connectionId - 连接ID
   * @param {string} detail - 详细信息
   * @private
   */
  _handleConnectionStatusChanged(isConnected, connectionId, detail) {
    console.log('AisDataReceiver连接状态变更:', isConnected, connectionId, detail || '已连接');
    
    // 更新连接状态
    const previousState = this._isConnected;
    this._isConnected = isConnected;
    
    // 如果连接断开后又重新连接，需要重新订阅
    if (isConnected && !this._isSubscribed) {
      // 使用延迟重新订阅，避免与其他组件竞争
      setTimeout(() => {
        if (this._isConnected && !this._isSubscribed) {
          console.log('AIS连接已恢复，尝试重新订阅...');
          this._subscribeWithRetry().then(() => {
            console.log('AisDataReceiver重新订阅成功');
          }).catch(err => {
            console.error('AisDataReceiver重新订阅失败:', err);
          });
        }
      }, 1000);
    } else if (!isConnected && previousState) {
      // 连接断开，更新订阅状态
      this._isSubscribed = false;
      console.log('AIS连接断开，已标记为未订阅');
    }
    
    // 触发连接状态变更事件
    eventBus.$emit('ais-connection-status-changed', {
      isConnected,
      connectionId,
      detail
    });
  }

  /**
   * 处理接收到的船舶消息
   * @param {Object} message - 接收到的消息
   * @param {Object} parsedData - 解析后的数据
   * @private
   */
  _handleShipMessage(message, parsedData) {
    // 检查消息是否是船舶数据
    if (message.type !== this._messageType) {
      return;
    }
    
    try {
      const shipData = parsedData;
      
      if (!shipData || !shipData.mmsi) {
        console.warn('AisDataReceiver: 无效的船舶数据:', shipData);
        return;
      }
      
      // 验证经纬度信息
      if (shipData.longitude === null || shipData.latitude === null) {
        console.debug('AisDataReceiver: 船舶数据缺少位置信息:', shipData.mmsi);
        return;
      }
      
      // 转换为标准船舶对象格式
      const ship = this._convertToShipObject(shipData);
      
      // 更新或添加船舶数据
      const existingShipIndex = this._ships.findIndex(s => s.userid === ship.userid);
      if (existingShipIndex >= 0) {
        this._ships[existingShipIndex] = ship;
      } else {
        this._ships.push(ship);
      }
      
      // 定期执行船舶出清（10秒一次），移除5分钟内没有收到新数据的船舶
      const now = Date.now();
      if (now - this._lastCleanupTime > this._cleanupThrottleInterval) {
        this._ships = cleanupExpiredShips(this._ships);
        this._lastCleanupTime = now;
      }
      
      // 准备事件数据
      const eventData = {
        ship,
        isNew: existingShipIndex < 0,
        ships: this._ships
      };
      
      // 使用节流机制发送更新
      const shouldThrottle = now - this._lastEmitTime < this._throttleInterval;
      
      // 如果启用了缓冲，并且地图未就绪或需要节流，则将更新添加到缓冲区
      if ((this._bufferEnabled && !this._mapReady) || shouldThrottle) {
        this._bufferUpdate(eventData);
      } else {
        // 否则直接发布事件
        this._emitShipDataUpdated(eventData);
        this._lastEmitTime = now;
      }
    } catch (error) {
      console.error('AisDataReceiver处理船舶数据失败:', error);
    }
  }
  
  /**
   * 缓冲船舶数据更新
   * @param {Object} eventData - 事件数据
   * @private
   */
  _bufferUpdate(eventData) {
    // 添加到待处理队列
    this._pendingUpdates.push(eventData);
    
    // 如果待处理队列过长，保留最新的100条
    if (this._pendingUpdates.length > 100) {
      this._pendingUpdates = this._pendingUpdates.slice(-100);
    }
    
    // 如果没有定时器在运行，启动一个
    if (!this._bufferTimer) {
      this._bufferTimer = setTimeout(() => {
        this._processBufferedUpdates();
        this._bufferTimer = null;
      }, this._updateInterval);
    }
  }
  
  /**
   * 处理缓冲的更新
   * @private
   */
  _processBufferedUpdates() {
    // 如果没有待处理的更新，直接返回
    if (this._pendingUpdates.length === 0) {
      return;
    }
    
    // 只发送最新的更新
    const latestUpdate = this._pendingUpdates[this._pendingUpdates.length - 1];
    
    // 清空待处理队列
    this._pendingUpdates = [];
    
    // 发布事件
    this._emitShipDataUpdated(latestUpdate);
    this._lastEmitTime = Date.now();
  }
  
  /**
   * 安全地发布船舶数据更新事件
   * @param {Object} eventData - 事件数据
   * @private
   */
  _emitShipDataUpdated(eventData) {
    try {
      // 即使地图未就绪，也发送事件，让组件自行决定如何处理
      eventBus.$emit('ship-data-updated', eventData);
    } catch (emitError) {
      console.error('AisDataReceiver: 发布船舶更新事件失败:', emitError);
      
      // 出现错误时，尝试通过setTimeout脱离当前调用栈再次发送，避免事件处理错误影响后续操作
      setTimeout(() => {
        try {
          eventBus.$emit('ship-data-updated', eventData);
        } catch (retryError) {
          console.error('AisDataReceiver: 重试发布船舶更新事件失败:', retryError);
        }
      }, 0);
    }
  }

  /**
   * 将原始数据转换为标准船舶对象
   * @param {Object} rawData - 原始船舶数据
   * @returns {Object} 标准船舶对象
   * @private
   */
  _convertToShipObject(rawData) {
    // 处理heading和cog
    let heading = parseFloat(rawData.heading);
    const cog = parseFloat(rawData.course || 0);
    
    // 检查heading是否有效（AIS规范中511表示无效值）
    if (isNaN(heading) || heading === 511) {
      // console.debug(`AisDataReceiver: 船舶 ${rawData.mmsi} 的heading无效(${heading})，使用cog(${cog})`);
      heading = null; // 将无效的heading设为null，让角度计算逻辑使用cog
    }
    
    return {
      userid: rawData.mmsi,
      msgid: 'AIS',
      // 清洗船名：去除 AIS 报文中的 @ 填充符与控制字符
      shipname: sanitizeVesselName(rawData.vesselCnName || rawData.name || rawData.vesselName) || rawData.mmsi,
      pos: {
        longitude: rawData.longitude,
        latitude: rawData.latitude
      },
      cog: cog,
      heading: heading,
      sog: parseFloat(rawData.speed || 0),
      shipType: rawData.shipType,
      length: (rawData.dimBow || 0) + (rawData.dimStern || 0),
      width: (rawData.dimPort || 0) + (rawData.dimStarboard || 0),
      receive_time: rawData.receivedTime || new Date().toISOString()
    };
  }

  /**
   * 设置缓冲状态
   * @param {boolean} enabled - 是否启用缓冲
   * @param {number} interval - 更新间隔（毫秒）
   */
  setBuffering(enabled, interval = 1000) {
    this._bufferEnabled = enabled;
    this._updateInterval = interval;
    
    console.log(`AisDataReceiver: 数据缓冲${enabled ? '已启用' : '已禁用'}, 间隔: ${interval}ms`);
    
    // 如果禁用缓冲，立即处理所有待处理的更新
    if (!enabled && this._pendingUpdates.length > 0) {
      this._processBufferedUpdates();
    }
  }

  /**
   * 设置地图就绪状态
   * @param {boolean} ready - 地图是否就绪
   */
  setMapReady(ready) {
    const wasReady = this._mapReady;
    this._mapReady = ready;
    
    console.log(`AisDataReceiver: 地图就绪状态设置为 ${ready}`);
    
    // 如果地图从未就绪变为就绪，处理所有待处理的更新
    if (!wasReady && ready && this._pendingUpdates.length > 0) {
      console.log(`AisDataReceiver: 地图就绪，处理${this._pendingUpdates.length}个待处理的更新`);
      this._processBufferedUpdates();
    }
  }

  /**
   * 设置节流间隔
   * @param {number} interval - 节流间隔（毫秒）
   */
  setThrottleInterval(interval) {
    this._throttleInterval = interval;
    console.log(`AisDataReceiver: 节流间隔设置为 ${interval}ms`);
  }

  /**
   * 断开连接并清理资源
   * @returns {Promise<void>}
   */
  async disconnect() {
    // 清除缓冲定时器
    if (this._bufferTimer) {
      clearTimeout(this._bufferTimer);
      this._bufferTimer = null;
    }
    
    // 清空待处理队列
    this._pendingUpdates = [];
    
    if (this._isSubscribed && this._signalR) {
      try {
        await this._signalR.unsubscribe(this._topic);
        this._isSubscribed = false;
        console.log('AisDataReceiver已取消订阅');
      } catch (err) {
        console.warn('AisDataReceiver取消订阅失败:', err);
      }
    }
  }

  /**
   * 获取当前船舶数据列表
   * @returns {Array} 船舶数据数组
   */
  get ships() {
    return [...this._ships]; // 返回副本，防止外部修改
  }

  /**
   * 获取连接状态
   * @returns {boolean} 是否已连接
   */
  get isConnected() {
    return this._isConnected;
  }

  /**
   * 获取订阅状态
   * @returns {boolean} 是否已订阅
   */
  get isSubscribed() {
    return this._isSubscribed;
  }

  /**
   * 获取船舶数量
   * @returns {number} 船舶数量
   */
  get shipCount() {
    return this._ships.length;
  }
}

// 事件监听器助手方法
const aisEvents = {
  /**
   * 监听船舶数据更新事件
   * @param {Function} callback - 回调函数
   */
  onShipDataUpdated(callback) {
    eventBus.$on('ship-data-updated', callback);
  },

  /**
   * 移除船舶数据更新事件监听
   * @param {Function} callback - 回调函数
   */
  offShipDataUpdated(callback) {
    eventBus.$off('ship-data-updated', callback);
  },

  /**
   * 监听连接状态变更事件
   * @param {Function} callback - 回调函数
   */
  onConnectionStatusChanged(callback) {
    eventBus.$on('ais-connection-status-changed', callback);
  },

  /**
   * 移除连接状态变更事件监听
   * @param {Function} callback - 回调函数
   */
  offConnectionStatusChanged(callback) {
    eventBus.$off('ais-connection-status-changed', callback);
  }
};

// 创建并导出单例实例
const aisDataReceiver = new AisDataReceiver();

export { aisDataReceiver, aisEvents };
export default aisDataReceiver; 